'use client';

import Image from 'next/image';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { detectRegion } from '../utils/regionColors';
import type { FeaturedPlacementSummary, FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';

export interface StoneInvestmentPropertyHeroProps {
  property: {
    id: string;
    name: string;
    description?: string | null;
    primaryImageUrl?: string | null;
    bedrooms?: number | null;
    bathrooms?: number | null;
    totalFloorArea?: number | null;
    currency?: string | null;
    attributeValues?: {
      askingPrice?: {
        valueNumber?: number | null;
      };
    } | null;
  };
  /** Featured placements for this property */
  featuredPlacements?: FeaturedPlacementSummary[];
  /** Whether to show the featured badge */
  showFeaturedBadge?: boolean;
  onClose?: () => void;
  locale?: string;
  messages?: Record<string, unknown>;
}

/**
 * Stone Investment Property Hero
 * 
 * Concept One Design (Exact Match):
 * - Full-screen hero with parallax scroll effect
 * - Gradient overlays
 * - Region-specific subtitles
 * - Large typography with price and location
 * - Back navigation button
 * - Progress bar at top
 */
export function StoneInvestmentPropertyHero({
  property,
  featuredPlacements = [],
  showFeaturedBadge = true,
  onClose,
  locale: _locale = 'en',
  messages,
}: StoneInvestmentPropertyHeroProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  // Detect region from description
  const region = detectRegion(property.description);
  const isFrance = region === 'provence';
  
  // Get primary featured badge
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  // Get price details for FormatPrice
  const askingPrice = property.attributeValues?.askingPrice?.valueNumber;
  const currency = property.currency || DEFAULT_CURRENCY;
  const hasPrice = askingPrice && askingPrice > 0;

  // Parallax & Scroll Progress Logic
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scroll = totalScroll / windowHeight;
      setScrollProgress(scroll);

      if (heroRef.current) {
        heroRef.current.style.transform = `translateY(${totalScroll * 0.5}px)`;
        heroRef.current.style.opacity = `${1 - totalScroll / 700}`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const heroImage = property.primaryImageUrl || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop';

  return (
    <>
      {/* Sticky Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-[60] bg-stone-200">
        <div 
          className="h-full bg-stone-900 transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* Navigation Controls */}
      {onClose && (
        <button 
          onClick={onClose}
          className="fixed top-8 left-8 z-[60] flex items-center gap-3 mix-blend-difference text-white hover:opacity-70 transition-opacity group"
        >
          <div className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center group-hover:bg-white group-hover:text-stone-900 transition-all">
            <ArrowLeft size={16} />
          </div>
          <span className="uppercase text-xs tracking-widest font-sans hidden md:block">{((messages?.stone as Record<string, unknown>)?.common as Record<string, string>)?.backToCollection || 'Back to Collection'}</span>
        </button>
      )}

      {/* --- HERO --- */}
      <header data-dark-hero="true" className="relative w-full h-screen overflow-hidden bg-stone-900">
        <div ref={heroRef} className="absolute inset-0 w-full h-full will-change-transform">
          <Image
            src={heroImage}
            alt={property.name}
            fill
            className="object-cover opacity-80"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-stone-900/90" />
        </div>

        {/* Featured Badge - Top Right */}
        {primaryFeatured?.badgeVariant && (
          <div className="absolute top-24 right-6 md:right-24 z-20">
            <FeaturedBadge
              variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
              label={primaryFeatured.label}
              size="lg"
            />
          </div>
        )}

        <div className="absolute bottom-24 left-6 md:left-24 right-6 text-white z-10">
          <div className="overflow-hidden mb-2">
            <p 
              className="font-sans text-xs md:text-sm uppercase tracking-[0.4em] opacity-0 animate-[fadeUp_1s_ease-out_0.2s_forwards]"
            >
              {isFrance
                ? (((messages?.stone as Record<string, unknown>)?.regions as Record<string, string>)?.provenceRegion || 'Provence-Alpes-Côte d\'Azur')
                : (((messages?.stone as Record<string, unknown>)?.regions as Record<string, string>)?.mauritiusRegion || 'Rivière Noire District')}
            </p>
          </div>
          <h1 
            className="font-serif text-5xl md:text-8xl lg:text-9xl leading-none opacity-0 animate-[fadeUp_1s_ease-out_0.4s_forwards]"
                >
                  {property.name}
                </h1>
          <div 
            className="mt-8 flex items-center gap-6 opacity-0 animate-[fadeUp_1s_ease-out_0.6s_forwards]"
          >
            {hasPrice && (
              <span className="text-xl md:text-2xl font-serif italic">
                <FormatPrice
                  amount={askingPrice}
                  currency={currency}
                  size="lg"
                  className="!text-white"
                />
              </span>
            )}
            <span className="h-px w-12 bg-white/50" />
            <span className="uppercase text-xs tracking-widest font-sans">
              {property.description || 'Luxury Property'}
            </span>
          </div>
        </div>
      </header>
    </>
  );
}
